import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * The access token lives in memory only. A refresh token is held in an httpOnly
 * cookie the browser sends automatically, so a successful XSS cannot read a
 * long-lived credential out of localStorage.
 */
let accessToken = null;
let onUnauthorized = null;

export const setAccessToken = (token) => {
  accessToken = token;
};
export const getAccessToken = () => accessToken;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

const client = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // send the refresh cookie
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/**
 * A single in-flight refresh shared by every request that hits a 401, so a page
 * issuing six parallel calls does not fire six refreshes and rotate the token
 * out from under itself.
 */
let refreshPromise = null;

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = client
      .post('/auth/refresh')
      .then((res) => {
        const payload = res.data?.data;
        setAccessToken(payload?.accessToken);
        return payload;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * The shared refresh, for callers outside the 401 interceptor.
 *
 * AuthContext needs this on boot, and it MUST go through the same in-flight promise:
 * the API rotates the refresh token and destroys the old one as it is exchanged, so two
 * concurrent refreshes mean the second presents a token that no longer exists and gets a
 * 401. React StrictMode double-invokes effects in development, which makes that happen
 * on every page load unless the two calls share one request.
 *
 * Resolves to the whole payload — `{ user, accessToken }` — because a caller restoring a
 * session needs the user, not just the token.
 */
export const restoreSession = () => refreshSession();

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    if (!response) {
      // No response at all — network down, DNS failure, or the API is not running.
      return Promise.reject(
        normaliseError(error, 'Cannot reach the server. Check your connection and try again.')
      );
    }

    const isAuthRoute = config?.url?.includes('/auth/login') || config?.url?.includes('/auth/refresh');

    if (response.status === 401 && !config._retried && !isAuthRoute) {
      config._retried = true;
      try {
        const payload = await refreshSession();
        const token = payload?.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          return client(config);
        }
      } catch {
        // Refresh itself failed — the session is genuinely over.
      }
      setAccessToken(null);
      if (onUnauthorized) onUnauthorized();
    }

    return Promise.reject(normaliseError(error));
  }
);

/**
 * Flattens the API error envelope into a predictable shape, so components never
 * have to reach into error.response.data themselves.
 */
function normaliseError(error, fallbackMessage) {
  const data = error.response?.data;
  const err = new Error(data?.message || fallbackMessage || error.message || 'Something went wrong');
  err.status = error.response?.status;
  err.code = data?.code;
  // Field-level validation failures, keyed for the form layer.
  err.fieldErrors = Array.isArray(data?.errors)
    ? data.errors.reduce((acc, e) => ({ ...acc, [e.field]: e.message }), {})
    : {};
  err.details = data?.errors || null;
  err.requestId = data?.requestId;
  return err;
}

/** Unwraps the `{ success, data, meta }` envelope for the caller. */
export const unwrap = (promise) =>
  promise.then((res) => ({ data: res.data?.data, meta: res.data?.meta, message: res.data?.message }));

export default client;
